/**
 * SPD (electronic percussion pad, e.g. Roland SPD-SX PRO) configuration
 * model + FRONT-VIEW SVG generation.
 *
 * A "SPD" is a SINGLE stage element (iconId: 'spd'). The on-stage graphic
 * is a horizontal rectangular pad unit showing 6 square trigger pads
 * (2 columns × 3 rows) on the left/centre, a small screen + control
 * buttons on the right, mounted on a SINGLE central stand leg — matched
 * to the reference Roland SPD-SX PRO silhouette.
 *
 * All coordinates are in a 0..100 viewBox space (square), so the same
 * generator feeds both the editor preview and the on-stage render.
 */

import { STAGE_SVG_GRAY } from './StageIcon';

export interface SpdConfig {
  /** Reserved; the unit always shows 6 pads in this renderer. */
  onStand: boolean;
}

export function defaultSpd(): SpdConfig {
  return { onStand: true };
}

const GROUND = 92; // shared with drumKitConfig — feet rest on the kick's base line
const STAND = '#000000';

/**
 * Build the inner SVG markup for a FRONT-VIEW SPD pad unit.
 * Returns markup inside a 0 0 100 100 viewBox.
 *
 * Layout keeps the trigger pads, screen and buttons at FIXED sizes and
 * packs them tightly inside a narrow body (centred) with a small margin
 * so no element touches another or the body edge.
 */
export function buildSpdSvg(cfg: SpdConfig | null | undefined, _color: string): string {
  const c = cfg && typeof cfg.onStand === 'boolean' ? cfg : defaultSpd();
  const parts: string[] = [];

  // ── SINGLE central stand leg + TRIPOD foot ──
  if (c.onStand) {
    const cx = 50;
    parts.push(
      `<line x1="${cx}" y1="${GROUND}" x2="${cx}" y2="70" stroke="${STAND}" stroke-width="1.8"/>`,
    );
    parts.push(
      `<path d="M${cx - 10} ${GROUND} L${cx} ${GROUND - 10} L${cx + 10} ${GROUND}" fill="none" stroke="${STAND}" stroke-width="1.4" stroke-linejoin="round"/>`,
    );
  }

  // ── Fixed-size pads: 3 columns × 3 rows (9 pads) ──
  const padW = 9;
  const padH = 5;
  const colGap = 3;
  const rowGap = 3;
  const cols = 3;
  const rows = 3;
  const m = 2; // inner margin inside the body

  const padsW = cols * padW + (cols - 1) * colGap; // 33
  const padsH = rows * padH + (rows - 1) * rowGap; // 21
  const contentW = padsW + 2 * m; // 37
  const contentH = padsH + 2 * m; // 25

  // ── Body (narrow square-ish rectangle, centred) ──
  const leftX = 50 - contentW / 2; // 31.5
  const topY = c.onStand ? 44 : 60;
  const bodyFill = STAGE_SVG_GRAY;
  parts.push(
    `<rect x="${leftX.toFixed(1)}" y="${topY}" width="${contentW}" height="${contentH}" rx="4" fill="${bodyFill}" stroke="#00000055" stroke-width="1.5"/>`,
  );

  // ── 9 SQUARE trigger pads: 3 columns × 3 rows ──
  const padX0 = leftX + m;
  const padY0 = topY + m;
  for (let r = 0; r < rows; r++) {
    for (let col = 0; col < cols; col++) {
      const px = padX0 + col * (padW + colGap);
      const py = padY0 + r * (padH + rowGap);
      parts.push(
        `<rect x="${px.toFixed(1)}" y="${py.toFixed(1)}" width="${padW}" height="${padH}" rx="1.5" fill="#5A5A5A" stroke="#00000066" stroke-width="0.6"/>`,
      );
    }
  }

  return parts.join('');
}

/**
 * Serialise an SpdConfig to an SVG STRING (viewBox 0 0 100 100)
 * for the on-stage render path.
 */
export function spdToSvg(cfg: SpdConfig | null | undefined, color: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100"><g>${buildSpdSvg(cfg, color)}</g></svg>`;
}
